// src/components/compliance/complianceDisplay.ts
//
// Shared display helpers for the Compliance UI. Keeping labels +
// tokens here so a rename in one place propagates and tests can pin
// exact copy.

import type {
  ComplianceApprovalStatus,
  ComplianceDocumentStatus,
  ComplianceEnforcementMode,
  ComplianceReminderMilestone,
  ComplianceSubjectType,
  EligibilitySeverity,
} from '../../types/api';

// ---------------------------------------------------------------------------
// Enforcement mode
// ---------------------------------------------------------------------------

export const ENFORCEMENT_MODE_LABEL: Record<ComplianceEnforcementMode, string> = {
  informational: 'Informational',
  warning: 'Warning',
  approval_required: 'Approval required',
  hard_block: 'Hard block',
};

export const ENFORCEMENT_MODE_DESCRIPTION: Record<ComplianceEnforcementMode, string> = {
  informational: 'Shown on dashboards. Never blocks any action.',
  warning: 'Surfaces as a warning. Assignments can still proceed.',
  approval_required: 'Evidence must be approved before the subject counts as compliant.',
  hard_block: 'Blocks work for anyone missing or with expired evidence.',
};

export const ENFORCEMENT_MODE_CHIP: Record<ComplianceEnforcementMode, string> = {
  informational: 'bg-slate-100 text-slate-700 ring-1 ring-slate-200',
  warning: 'bg-amber-100 text-amber-800 ring-1 ring-amber-200',
  approval_required: 'bg-violet-100 text-violet-800 ring-1 ring-violet-200',
  hard_block: 'bg-red-100 text-red-800 ring-1 ring-red-300',
};

// ---------------------------------------------------------------------------
// Eligibility severity
// ---------------------------------------------------------------------------

export const SEVERITY_LABEL: Record<EligibilitySeverity, string> = {
  ok: 'Compliant',
  warning: 'Warning',
  blocked: 'Blocked',
};

export const SEVERITY_CHIP: Record<EligibilitySeverity, string> = {
  ok: 'bg-emerald-100 text-emerald-800 ring-1 ring-emerald-200',
  warning: 'bg-amber-100 text-amber-800 ring-1 ring-amber-200',
  blocked: 'bg-red-100 text-red-800 ring-1 ring-red-300',
};

// ---------------------------------------------------------------------------
// Document status
// ---------------------------------------------------------------------------

export const DOC_STATUS_LABEL: Record<ComplianceDocumentStatus, string> = {
  pending_upload: 'Pending upload',
  draft: 'Draft',
  pending_approval: 'Pending approval',
  approved: 'Approved',
  rejected: 'Rejected',
  active: 'Active',
  expiring: 'Expiring',
  expired: 'Expired',
  renewed: 'Renewed',
  archived: 'Archived',
  blocked: 'Blocked',
};

export const DOC_STATUS_CHIP: Record<ComplianceDocumentStatus, string> = {
  pending_upload: 'bg-slate-100 text-slate-700 ring-1 ring-slate-300',
  draft: 'bg-slate-100 text-slate-700 ring-1 ring-slate-200',
  pending_approval: 'bg-amber-100 text-amber-800 ring-1 ring-amber-200',
  approved: 'bg-emerald-100 text-emerald-800 ring-1 ring-emerald-200',
  rejected: 'bg-red-100 text-red-800 ring-1 ring-red-300',
  active: 'bg-emerald-100 text-emerald-800 ring-1 ring-emerald-200',
  expiring: 'bg-amber-100 text-amber-800 ring-1 ring-amber-200',
  expired: 'bg-red-100 text-red-800 ring-1 ring-red-300',
  renewed: 'bg-emerald-100 text-emerald-800 ring-1 ring-emerald-200',
  archived: 'bg-slate-100 text-slate-600 ring-1 ring-slate-200',
  blocked: 'bg-red-100 text-red-800 ring-1 ring-red-300',
};

// ---------------------------------------------------------------------------
// Approval status
// ---------------------------------------------------------------------------

export const APPROVAL_STATUS_LABEL: Record<ComplianceApprovalStatus, string> = {
  pending: 'Pending',
  approved: 'Approved',
  rejected: 'Rejected',
  delegated: 'Delegated',
  cancelled: 'Cancelled',
};

export const APPROVAL_STATUS_CHIP: Record<ComplianceApprovalStatus, string> = {
  pending: 'bg-amber-100 text-amber-800 ring-1 ring-amber-200',
  approved: 'bg-emerald-100 text-emerald-800 ring-1 ring-emerald-200',
  rejected: 'bg-red-100 text-red-800 ring-1 ring-red-300',
  delegated: 'bg-violet-100 text-violet-800 ring-1 ring-violet-200',
  cancelled: 'bg-slate-100 text-slate-600 ring-1 ring-slate-200',
};

// ---------------------------------------------------------------------------
// Subject type
// ---------------------------------------------------------------------------

export const SUBJECT_TYPE_LABEL: Record<ComplianceSubjectType, string> = {
  contact: 'Contact',
  provider_user: 'Provider / User',
  provider_role: 'Provider Role',
  department_circle: 'Department Circle',
  central_team: 'Central Team',
  workspace: 'Whole workspace',
};

// ---------------------------------------------------------------------------
// Reminder milestone
// ---------------------------------------------------------------------------

export const MILESTONE_LABEL: Record<ComplianceReminderMilestone, string> = {
  '90_days': '90 days out',
  '60_days': '60 days out',
  '30_days': '30 days out',
  '14_days': '14 days out',
  '7_days': '7 days out',
  expired: 'Expired',
};

// ---------------------------------------------------------------------------
// Dates
// ---------------------------------------------------------------------------

export function formatShortDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

export function daysBetween(from: Date, to: Date): number {
  const ms = to.getTime() - from.getTime();
  return Math.ceil(ms / (24 * 60 * 60 * 1000));
}

export function daysUntil(iso: string | null | undefined, now: Date = new Date()): number | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return daysBetween(now, d);
}

// ---------------------------------------------------------------------------
// Error mapping — read messages from the compliance API 4xx/5xx bodies
// ---------------------------------------------------------------------------

export interface ComplianceErrorLike {
  status?: number;
  data?: unknown;
}

/**
 * Convert an RTK Query error into a business-readable message. Uses
 * the backend's `code` field when present so the same underlying
 * failure renders the same string across surfaces.
 */
export function messageFromComplianceError(err: unknown, fallback = 'Something went wrong.'): string {
  const e = err as ComplianceErrorLike | undefined;
  if (!e) return fallback;
  const status = e.status ?? null;
  const body = (e.data ?? {}) as { error?: string; code?: string; message?: string };
  const code = body.code;
  const backend = body.error || body.message;

  if (status === 503 && code === 'central_documents_unavailable') {
    return 'Document upload is not available until the Central Document Platform is enabled.';
  }
  if (status === 401) return 'You need to sign in again to continue.';
  if (status === 403) {
    if (code === 'approval_forbidden') {
      return 'You are not authorized to act on this approval. Approver rights come from an explicit assignment or the compliance.approve capability.';
    }
    return backend || 'You do not have permission to perform this action.';
  }
  if (status === 404) return backend || 'This item no longer exists.';
  if (status === 409) return backend || 'Conflict — this action cannot be completed with the current state.';
  if (status === 400 || status === 422) {
    switch (code) {
      case 'invalid_transition':
        return backend || 'That state change is not allowed from the current status.';
      case 'already_pending':
        return 'This document already has a pending approval request.';
      case 'already_attached':
        return 'This document already has evidence attached.';
      case 'reason_required':
        return 'Please provide a reason.';
      case 'invalid_enforcement_mode':
        return 'That enforcement mode is not recognized.';
      case 'lead_exceeds_validity':
        return 'Renewal lead days cannot exceed the validity window.';
      case 'invalid_validity':
      case 'invalid_lead':
        return 'Validity and lead days must be non-negative.';
      case 'requirement_in_use':
        return 'This requirement is still in use. Archive its assignments and documents first.';
      case 'duplicate_assignment':
        return 'This subject already has this requirement assigned.';
      case 'subject_not_found':
        return 'The subject you selected could not be found in this workspace.';
      case 'subject_mismatch':
        return 'Subject and workspace do not match.';
      case 'category_not_found':
        return 'That category does not exist in this workspace.';
      case 'category_inactive':
        return 'That category is inactive.';
      case 'requirement_not_found':
        return 'That requirement does not exist in this workspace.';
      case 'requirement_inactive':
        return 'That requirement is inactive.';
      case 'terminal_status':
        return 'This document is in a terminal status and cannot be edited.';
      case 'no_evidence':
        return 'Evidence must be attached before submitting for approval.';
      case 'system_category_locked':
        return 'System categories are locked; deactivate instead.';
      case 'invalid_source_state':
        return 'This document cannot be moved into approval from its current status.';
      case 'not_pending':
        return 'This approval is no longer pending.';
      case 'invalid_delegate':
        return 'You cannot delegate an approval to yourself.';
      case 'invalid_subject_type':
        return 'That subject type is not supported.';
      default:
        return backend || fallback;
    }
  }
  return backend || fallback;
}
